//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { GridRowsProp } from '@mui/x-data-grid-premium'
import { EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'
import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { PoolFlexOverviewCTO, PoolReportingAccountCTOSchema } from '@shared/sharedTypes'
import { OtStoreState, useOtChainState, useOtSelector } from '@react/redux/store'
import { PoolTypeEnum } from '@shared/Enums'
import { ReportDataShape } from '@shared/ReportDefinitionCTO'
import dayjs, { Dayjs } from 'dayjs'
import ReportExplorer from './ReportExplorer'
import { fetchAllPoolAccountAccessPairs, fetchDailyPoolAccountCTOs } from '@react/fetchers/PoolFetchers'
import { logOT } from '@shared/loggingShared'
import { fetchAllOffchainData } from './reportingUtils'
import { PoolOffchainDataCTO, PoolOffchainDataReportingSchema } from '@shared/PoolOffchainDataCTO'
import { useDispatch } from 'react-redux'
import { poolActions } from '@react/redux/poolSlice'

interface LenderTimePeriodPageFlexProps {
  isLender: boolean
}

export const LenderExplorerSchema = yup.object({
  poolAccountCTO: PoolReportingAccountCTOSchema(),
  poolOffchainDataCTO: PoolOffchainDataReportingSchema(),
})

export default function LenderTimePeriodPageFlex({ isLender }: LenderTimePeriodPageFlexProps): JSX.Element {
  const [loading, setLoading] = useState<boolean>(false)
  const [fetchDataError, setFetchDataError] = useState<any | undefined>(undefined)
  const [rowsForAccounts, setRowsForAccounts] = useState<GridRowsProp>([])

  const {
    userConnection,
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { chainName },
  } = useOtChainState()

  const poolMergedOverviewCTOs = useOtSelector((state) => state.poolState.poolOverviewCTOs)
  const poolOffchainDataCTOs = useOtSelector<OtStoreState, Record<EthAddr, PoolOffchainDataCTO>>(
    (state: OtStoreState) => state.poolState.poolOffchainDataCTOs
  )

  const [storedPoolOffchainDataCTOs, setStoredPoolOffchainDataCTOs] = useState<Record<EthAddr, PoolOffchainDataCTO>>({})
  const dispatch = useDispatch()
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | undefined>(undefined)
  const [lenderAddr, setLenderAddr] = useState<EthAddr | undefined>(undefined)
  const [poolAccountAccessPairs, setPoolAccountAccessPairs] = useState<PoolAccountAccessPair[]>([])
  const [poolAddr, setPoolAddr] = useState<EthAddr | undefined>(undefined)

  const [lenderAddrs, setLenderAddrs] = useState<EthAddr[]>([])
  const [poolAddrs, setPoolAddrs] = useState<EthAddr[]>([])

  const poolFlexOverviewCTOs: PoolFlexOverviewCTO[] = Object.values(poolMergedOverviewCTOs).filter((pool) => {
    return pool.poolType === PoolTypeEnum.FlexibleRate
  }) as PoolFlexOverviewCTO[]

  useEffect(
    function setPoolAddrsEffect() {
      const _poolAddrs = poolFlexOverviewCTOs.map((pool) => pool.poolAddr)
      if (_poolAddrs.length != poolAddrs.length) {
        setPoolAddrs(_poolAddrs)
      }
    },
    [poolFlexOverviewCTOs]
  )
  useEffect(
    function setLenderIfIsLenderEffect() {
      if (isLender) {
        const lenderAddr = userConnection?.accountAddr as EthAddr
        setLenderAddr(lenderAddr)
      }
    },
    [isLender]
  )
  useEffect(function initializeDatesEffect() {
    if (chainName === 'LocalHardhat') {
      const startDate = dayjs.tz('2025-04-10', 'UTC').startOf('day')
      const endDate = dayjs.tz('2025-04-12', 'UTC').startOf('day')
      setDateRangeFromExplorer([startDate, endDate])
    } else {
      const startDate = dayjs().tz('UTC').startOf('day')
      const endDate = dayjs().tz('UTC').startOf('day')
      setDateRangeFromExplorer([startDate, endDate])
    }
  }, [])

  useEffect(
    function fetchLenderAddrsEffect() {
      logOT.debug('fetchLenderAddrsEffect', serverApiBaseUrl, restAPIKey, poolAddrs.length)
      if (serverApiBaseUrl && restAPIKey && poolAddrs.length > 0) {
        fetchAllPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey)
          .then((poolAccountAccessPairs) => {
            const _poolAccountAccessPairs = poolAccountAccessPairs.filter((pair) => poolAddrs.includes(pair.poolAddr))
            const participantSet = new Set<EthAddr>()
            _poolAccountAccessPairs.forEach((pair) => {
              participantSet.add(pair.accountAddr)
            })
            setPoolAccountAccessPairs(_poolAccountAccessPairs)
            setLenderAddrs(Array.from(participantSet))
          })
          .catch((e) => {
            logOT.error('Error getting lenders', e)
          })
      }
    },
    [serverApiBaseUrl, poolAddrs]
  )

  useEffect(
    function fetchOffchainDataEffect() {
      if (restAPIKey && serverApiBaseUrl) {
        fetchAllOffchainData(serverApiBaseUrl, restAPIKey, Object.keys(poolMergedOverviewCTOs), poolOffchainDataCTOs)
          .then(
            (result: {
              fetchedPoolOffchainData: PoolOffchainDataCTO[]
              poolOffchainDataForPoolAddrs: Record<EthAddr, PoolOffchainDataCTO>
            }) => {
              result.fetchedPoolOffchainData.forEach((poolOffchainDataCTO) => {
                dispatch(poolActions.updatePoolOffchainDataCTO(poolOffchainDataCTO))
              })
              logOT.debug('## fetchedPoolOffchainData', result.fetchedPoolOffchainData)
              setStoredPoolOffchainDataCTOs(result.poolOffchainDataForPoolAddrs)
            }
          )
          .catch((e) => {
            logOT.error('Error getting lenders', e)
          })
      }
    },
    [serverApiBaseUrl, poolMergedOverviewCTOs]
  )

  const refreshData = () => {
    async function buildRowsEffect(): Promise<void> {
      try {
        setLoading(true)
        setFetchDataError(undefined)

        const poolAccountLocalRows: any[] = []

        if (poolAddr === undefined && lenderAddr === undefined) {
          for (let i = 0; i < lenderAddrs.length; i++) {
            const lenderAddr = lenderAddrs[i]
            const poolAddrsForLender = poolAccountAccessPairs.filter((pair) => pair.accountAddr === lenderAddr)
            for (let j = 0; j < poolAddrsForLender.length; j++) {
              const poolAddr = poolAddrsForLender[j].poolAddr
              const { accountRows } = await addRowsForLenderPoolPair(poolAddr, lenderAddr, poolAccountLocalRows)
              poolAccountLocalRows.push(...accountRows)
            }
          }
        } else if (lenderAddr === undefined && poolAddr) {
          for (let i = 0; i < lenderAddrs.length; i++) {
            const lenderAddr = lenderAddrs[i]
            const { accountRows } = await addRowsForLenderPoolPair(poolAddr, lenderAddr, poolAccountLocalRows)
            poolAccountLocalRows.push(...accountRows)
          }
        } else if (poolAddr === undefined && lenderAddr) {
          for (let j = 0; j < poolAddrs.length; j++) {
            const poolAddr = poolAddrs[j]
            const { accountRows } = await addRowsForLenderPoolPair(poolAddr, lenderAddr, poolAccountLocalRows)
            poolAccountLocalRows.push(...accountRows)
          }
        } else if (poolAddr && lenderAddr) {
          const { accountRows } = await addRowsForLenderPoolPair(poolAddr, lenderAddr, poolAccountLocalRows)
          poolAccountLocalRows.push(...accountRows)
        }

        setRowsForAccounts(poolAccountLocalRows)
      } catch (e: any) {
        setFetchDataError(e)
      } finally {
        setLoading(false)
      }
    }
    async function addRowsForLenderPoolPair(
      poolAddr: string,
      lenderAddr: string,
      poolAccountLocalRows: any[]
    ): Promise<{ accountRows: GridRowsProp }> {
      const accountRows = []

      const poolOffchainDataCTO = storedPoolOffchainDataCTOs[poolAddr]
      // const startDateTimestamp = startDate.getTime()
      // logOT.info('startDateTimestamp', startDateTimestamp, startDateTimestamp % (3600 * 24 * 1000))

      const startDateStr = dateRange && dateRange.length > 1 ? dateRange[0].format('YYYY-MM-DD') : undefined
      const endDateStr = dateRange && dateRange.length > 1 ? dateRange[1].format('YYYY-MM-DD') : undefined

      logOT.info('Dynamic ', startDateStr, endDateStr)
      if (serverApiBaseUrl && restAPIKey) {
        const poolAccountCTOs = await fetchDailyPoolAccountCTOs(
          serverApiBaseUrl,
          restAPIKey,
          poolAddr,
          lenderAddr,
          startDateStr,
          endDateStr
        )
        if (poolAccountCTOs) {
          for (let k = 0; k < poolAccountCTOs.length; k++) {
            const poolAccountCTO = poolAccountCTOs[k]
            accountRows.push({
              id: accountRows.length + poolAccountLocalRows.length,
              poolAccountCTO,
              poolOffchainDataCTO,
            })
          }
        }
      }

      return { accountRows }
    }
    buildRowsEffect()
  }

  function setDateRangeFromExplorer(dateRange: [Dayjs, Dayjs]): void {
    setRowsForAccounts([])
    setDateRange(dateRange)
  }

  const filteredPoolAddrs = Object.keys(poolMergedOverviewCTOs).filter((key) => {
    const poolOverviewCTO = poolMergedOverviewCTOs[key]
    return poolOverviewCTO.poolType === PoolTypeEnum.FlexibleRate
  })

  return (
    <>
      <ReportExplorer
        loading={loading}
        fetchDataError={fetchDataError}
        reportDataShape={
          isLender ? ReportDataShape.LENDER_TIME_PERIOD_FLEX_LENDER : ReportDataShape.LENDER_TIME_PERIOD_FLEX
        }
        rows={rowsForAccounts}
        dateRange={dateRange}
        setDateRange={setDateRangeFromExplorer}
        poolAddr={poolAddr}
        setPoolAddr={setPoolAddr}
        poolAddrs={filteredPoolAddrs}
        lenderAddrs={lenderAddrs}
        lenderAddr={lenderAddr}
        setLenderAddr={setLenderAddr}
        refreshData={refreshData}
      />
    </>
  )
}
